import { Movie } from '../types';

// Keywords for different types of searches
const SEO_KEYWORDS = {
  general: [
    'watch movies online',
    'download movies',
    'free movies',
    'latest movies',
    'HD movies',
    'movie download site',
    'online movies',
    'stream movies',
    'full movies',
    'movie streaming platform'
  ],
  quality: [
    '1080p',
    '720p',
    '4K',
    'HD quality',
    'high definition',
    'ultra HD',
    'best quality'
  ],
  action: [
    'watch',
    'download',
    'stream',
    'get',
    'view',
    'play'
  ]
};

// Generate dynamic meta description
export const generateMetaDescription = (movie?: Movie): string => {
  if (movie) {
    return `Watch and download ${movie.title} (${movie.release_date.split('-')[0]}) in HD quality. Stream ${movie.title} online free. ${movie.overview.slice(0, 100)}...`;
  }
  return 'Watch and download the latest movies in HD quality. Movies4Load offers free movie streaming and downloads. Find your favorite movies and TV shows all in one place.';
};

// Generate dynamic keywords
export const generateKeywords = (movie?: Movie): string => {
  const baseKeywords = [...SEO_KEYWORDS.general, ...SEO_KEYWORDS.quality];
  
  if (movie) {
    const movieSpecificKeywords = [
      movie.title,
      `${movie.title} movie`,
      `${movie.title} download`,
      `${movie.title} watch online`,
      `${movie.title} ${movie.release_date.split('-')[0]}`,
      ...movie.genres.map(genre => `${genre.name} movies`),
      ...SEO_KEYWORDS.action.map(action => `${action} ${movie.title}`),
      ...SEO_KEYWORDS.quality.map(quality => `${movie.title} ${quality}`)
    ];
    return [...movieSpecificKeywords, ...baseKeywords].join(', ');
  }

  return baseKeywords.join(', ');
};

// Generate structured data for movies
export const generateMovieStructuredData = (movie: Movie) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.overview,
    image: `https://image.tmdb.org/t/p/w500${movie.poster_path}`,
    datePublished: movie.release_date,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.vote_average,
      ratingCount: movie.vote_count,
      bestRating: '10',
      worstRating: '1'
    },
    director: movie.credits?.crew.find(person => person.job === 'Director')?.name,
    actor: movie.credits?.cast.slice(0, 5).map(actor => ({
      '@type': 'Person',
      name: actor.name
    }))
  };
};

// Generate dynamic canonical URLs
export const generateCanonicalUrl = (path: string): string => {
  const baseUrl = 'https://movies4load.com'; // Replace with your actual domain
  return `${baseUrl}${path}`;
};

// Generate sitemap entries for movies
export const generateMovieSitemapEntry = (movie: Movie) => {
  const baseUrl = 'https://movies4load.com'; // Replace with your actual domain
  return {
    url: `${baseUrl}/movie/${movie.id}/${movie.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    lastmod: new Date().toISOString(),
    changefreq: 'weekly',
    priority: 0.8
  };
};

// Generate robots.txt content
export const generateRobotsTxt = () => {
  return `
User-agent: *
Allow: /
Disallow: /api/
Disallow: /admin/
Disallow: /private/

Sitemap: https://movies4load.com/sitemap.xml
`;
};

// Helper function to create SEO-friendly URLs
export const createSeoFriendlyUrl = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Generate Open Graph tags
export const generateOpenGraphTags = (movie?: Movie) => {
  if (movie) {
    return {
      'og:title': `${movie.title} - Watch and Download in HD`,
      'og:description': generateMetaDescription(movie),
      'og:image': `https://image.tmdb.org/t/p/w500${movie.poster_path}`,
      'og:type': 'video.movie',
      'og:site_name': 'Movies4Load',
      'og:url': generateCanonicalUrl(`/movie/${movie.id}/${createSeoFriendlyUrl(movie.title)}`),
    };
  }

  return {
    'og:title': 'Movies4Load - Watch and Download Movies in HD',
    'og:description': generateMetaDescription(),
    'og:image': '/logo.png',
    'og:type': 'website',
    'og:site_name': 'Movies4Load',
    'og:url': generateCanonicalUrl('/'),
  };
};

// Generate Twitter Card tags
export const generateTwitterTags = (movie?: Movie) => {
  return {
    'twitter:card': 'summary_large_image',
    'twitter:site': '@movies4load',
    'twitter:title': movie ? `${movie.title} - Watch and Download in HD` : 'Movies4Load - HD Movies',
    'twitter:description': generateMetaDescription(movie),
    'twitter:image': movie ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : '/logo.png',
  };
};

// SEO optimization tips and best practices
export const seoOptimizationTips = {
  contentOptimization: [
    'Use movie titles in headings (H1, H2)',
    'Include movie descriptions',
    'Add alt text to images',
    'Use descriptive URLs',
    'Create unique meta descriptions',
    'Implement breadcrumbs navigation'
  ],
  technicalOptimization: [
    'Optimize page load speed',
    'Ensure mobile responsiveness',
    'Implement SSL certificate',
    'Create XML sitemap',
    'Set up robots.txt',
    'Use canonical URLs'
  ],
  linkBuilding: [
    'Internal linking between movies',
    'Link to genre pages',
    'Create movie collections',
    'Link to related movies'
  ]
}; 